import type { FlowAlertAiPatch } from './flowAlertSettingsStore';

const MARKER = 'MMEC_ALERT_CONFIG';

/**
 * Extract alert JSON from assistant message (marker or fenced json block).
 */
export function extractAlertConfigFromMessage(
  content: string,
): { cleanMessage: string; patch: FlowAlertAiPatch | null } {
  if (!content?.trim()) {
    return { cleanMessage: content, patch: null };
  }

  let patch: FlowAlertAiPatch | null = null;
  let clean = content;

  const markerIdx = content.indexOf(MARKER);
  if (markerIdx >= 0) {
    const jsonStart = markerIdx + MARKER.length;
    const rest = content.slice(jsonStart).trim();
    const parsed = tryParseJsonObject(rest);
    if (parsed) {
      patch = parsed;
      clean =
        content.slice(0, markerIdx).trimEnd() +
        (content.slice(jsonStart + rest.length).trim() ? '' : '');
    }
  }

  if (!patch) {
    const fence = /```(?:json)?\s*([\s\S]*?)\s*```/gi;
    let match: RegExpExecArray | null;
    while ((match = fence.exec(content)) !== null) {
      const parsed = tryParseJsonObject(match[1]);
      if (parsed && looksLikeAlertConfig(parsed)) {
        patch = parsed;
        clean = content.replace(match[0], '').trim();
        break;
      }
    }
  }

  return { cleanMessage: clean.trim(), patch };
}

function tryParseJsonObject(raw: string): FlowAlertAiPatch | null {
  const s = raw.trim();
  if (!s.startsWith('{')) {
    const brace = s.indexOf('{');
    if (brace < 0) return null;
    return tryParseJsonObject(s.slice(brace));
  }
  try {
    const end = findJsonEnd(s);
    const slice = end > 0 ? s.slice(0, end + 1) : s;
    return JSON.parse(slice) as FlowAlertAiPatch;
  } catch {
    return null;
  }
}

function findJsonEnd(s: string): number {
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === '\\') esc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === '{') depth++;
    else if (c === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function looksLikeAlertConfig(obj: FlowAlertAiPatch): boolean {
  return (
    Array.isArray(obj.rules) ||
    obj.notificationEnabled !== undefined ||
    obj.minPremiumFloor !== undefined ||
    obj.clear_rules === true ||
    obj.soundEnabled !== undefined
  );
}
